<!-- View a twin property value -->
<template lang="pug">
component(
  v-if="preview ? registeredRenderer?.viewComponentTable : registeredRenderer?.viewComponentDetail",
  :is="preview ? registeredRenderer?.viewComponentTable : registeredRenderer?.viewComponentDetail",
  :value="model"
)
template(v-else-if="prop.type === 'twinRelation'")
  p.flex.flex-row.smx-comma-separated-row.flex-wrap
    TwinField(
      v-for="twin in model",
      :key="twin.uuid",
      :twin="twin",
      @click.stop
    )
UsersField(v-else-if="prop.type === 'userRelationArray'", :user-ids="model")
LinkPropertyView(v-else-if="isLinkProperty", :value="model")
span(v-else-if="prop.constraint?.type === 'date'") {{ model ? formatDate(model) : "-" }}
span(v-else-if="prop.constraint?.type === 'year'") {{ model ? formatYear(model) : "-" }}

DurationPicker(
  v-else-if="prop.constraint?.type === 'duration'",
  :model-value="model",
  display
)
span(v-else-if="prop.constraint?.type === 'boolean'") {{ model ? formatYesNo(model) : "-" }}
p.whitespace-pre-wrap(v-else) {{ model !== undefined ? model : "-" }}
</template>

<script setup lang="ts">
import {
  isLinkPropertyValue,
  type TwinProperty,
} from "@shared/services/twinManagement";
import type { TwinManagementTypes } from "@inctec/smx-ui-client";
import { useTwinStore } from "@shared/stores/twinManagement";
import { getRenderer } from "@shared/services/propertyRendererRegistry";
import { formatDate } from "@shared/util/formatting";
import dayjs from "dayjs";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import TwinField from "./TwinField.vue";
import UsersField from "./UsersField.vue";
import DurationPicker from "@shared/components/DurationPicker.vue";
import LinkPropertyView from "./LinkPropertyView.vue";

type SearchTwinProperty = NonNullable<
  NonNullable<
    TwinManagementTypes.SearchTwinsResponse["result"]
  >[number]["properties"]
>[number];

function formatYear(date: string) {
  return dayjs(date).year();
}

const { t } = useI18n();

function formatYesNo(booleanString: string) {
  return booleanString === "true" ? t("yes") : t("no");
}

const props = defineProps<{
  prop: TwinProperty | SearchTwinProperty;
  modelValue?: any;
  preview?: boolean;
}>();

const emit = defineEmits(["update:modelValue"]);

const model = computed({
  get() {
    return props.modelValue !== undefined
      ? props.modelValue
      : props.prop.propertyValue?.value;
  },
  set(val) {
    emit("update:modelValue", val);
  },
});
const twinStore = useTwinStore();

onMounted(() => {
  void twinStore.getTwinTemplates();
});

const registeredRenderer = computed(() => {
  const templatePropUuid = props.prop.propertyTemplateUuid ?? props.prop.uuid;
  const twinType = twinStore.templateByPropertyUuid.get(templatePropUuid);
  return getRenderer(twinType, props.prop.customId);
});

const isLinkProperty = computed(
  () =>
    props.prop.type === "json" &&
    (props.prop.constraint?.type === "link" ||
      isLinkPropertyValue(model.value)),
);
</script>
